import { useState, useEffect } from 'react';
import { useProducts } from '@/hooks/useProducts';
import { useUserProfile } from '@/hooks/useUserProfile';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Package } from 'lucide-react';
import type { Product } from '@/contexts/ProductContext';
import { toast } from 'sonner';

interface ProductFormProps {
  open: boolean;
  onClose: () => void;
  product: Product | null;
}

export function ProductForm({ open, onClose, product }: ProductFormProps) {
  const { createProduct, updateProduct, uploadLogo } = useProducts();
  const { tenant } = useUserProfile();
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'digital' as any,
    product_type: 'ebook' as any,
    target_audience: 'b2c' as any,
    data_source: 'manual' as any,
    gateway_product_id: '',
    logo_url: '',
  });

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name,
        category: product.category,
        product_type: product.product_type,
        target_audience: product.target_audience,
        data_source: product.data_source || 'manual',
        gateway_product_id: product.gateway_product_id || '',
        logo_url: product.logo_url || '',
      });
      setLogoPreview(product.logo_url || null);
    } else {
      setFormData({
        name: '',
        category: 'digital',
        product_type: 'ebook',
        target_audience: 'b2c',
        data_source: 'manual',
        gateway_product_id: '',
        logo_url: '',
      });
      setLogoPreview(null);
    }
    setLogoFile(null);
  }, [product, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    let logoUrl = formData.logo_url;

    // Se houver arquivo novo, fazer upload
    if (logoFile && tenant?.id) {
      try {
        logoUrl = await uploadLogo(logoFile, tenant.id);
      } catch (error) {
        toast.error('Erro ao fazer upload do logo');
        return;
      }
    }

    const productData = {
      ...formData,
      logo_url: logoUrl || null,
      gateway_product_id: formData.gateway_product_id || null,
    };

    if (product) {
      await updateProduct.mutateAsync({ 
        id: product.id, 
        updates: productData 
      });
    } else {
      await createProduct.mutateAsync(productData as any);
    }
    
    handleClose();
  };

  const handleClose = () => {
    setLogoFile(null);
    setLogoPreview(null);
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar tamanho (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('O arquivo deve ter no máximo 2MB');
        return;
      }
      
      // Validar tipo
      if (!file.type.startsWith('image/')) {
        toast.error('Apenas imagens são permitidas');
        return;
      }
      
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {product ? 'Editar Produto' : 'Novo Produto'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome do Produto *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Nome do produto"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">Categoria *</Label>
            <Select
              value={formData.category}
              onValueChange={(value: any) => setFormData({ ...formData, category: value })}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="physical">Físico</SelectItem>
                <SelectItem value="digital">Digital</SelectItem>
                <SelectItem value="service">Serviço</SelectItem>
                <SelectItem value="subscription">Assinatura</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="product_type">Tipo *</Label>
            <Select
              value={formData.product_type}
              onValueChange={(value: any) => setFormData({ ...formData, product_type: value })}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ebook">E-book</SelectItem>
                <SelectItem value="course">Curso</SelectItem>
                <SelectItem value="saas">SaaS</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="target_audience">Público-Alvo *</Label>
            <Select
              value={formData.target_audience}
              onValueChange={(value: any) => setFormData({ ...formData, target_audience: value })}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="b2c">B2C (Consumidor Final)</SelectItem>
                <SelectItem value="b2b">B2B (Empresas)</SelectItem>
                <SelectItem value="b2b2c">B2B2C (Empresas e Consumidor)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="data_source">Fonte de Dados *</Label>
            <Select
              value={formData.data_source}
              onValueChange={(value: any) => setFormData({ ...formData, data_source: value })}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
            <SelectContent>
              <SelectItem value="manual">Manual</SelectItem>
              <SelectItem value="integration">Integração</SelectItem>
              <SelectItem value="upload">Importar</SelectItem>
            </SelectContent>
            </Select>
          </div>

          {formData.data_source === 'integration' && (
            <div className="space-y-2">
              <Label htmlFor="gateway_product_id">ID Gateway</Label>
              <Input
                id="gateway_product_id"
                value={formData.gateway_product_id}
                onChange={(e) => setFormData({ ...formData, gateway_product_id: e.target.value })}
                placeholder="ID do produto no gateway"
              />
              <p className="text-xs text-muted-foreground">
                Identificador do produto no gateway de pagamento
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="logo">Logo</Label>
            <div className="flex items-center gap-4">
              {logoPreview && (
                <Avatar className="h-16 w-16">
                  <AvatarImage src={logoPreview} alt="Logo preview" />
                  <AvatarFallback className="bg-primary/10">
                    <Package className="h-8 w-8 text-primary" />
                  </AvatarFallback>
                </Avatar>
              )}
              <div className="flex-1">
                <Input
                  id="logo"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  PNG, JPG ou WEBP até 2MB
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={createProduct.isPending || updateProduct.isPending}>
              {product ? 'Salvar' : 'Criar Produto'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
